// =============================================================================
// Section-header typewriter — reveal the slanted Caveat accent character-by-character when it
// scrolls into view (IntersectionObserver, once per element).
//
// snippets/section-header.liquid renders `.section-header__accent` with the FULL slogan text and a
// `data-animate-accent` hook ('true' when the block's `animate_accent` setting is on AND the accent
// has text). For each opted-in accent this module, on first scroll-into-view:
//   • splits the accent's text into per-character <span>s — walking the DOM so inline_richtext
//     markup (<em>/<strong>/<a>) is preserved, not corrupted by an innerHTML char-split,
//   • hides them via the `--pending` OPACITY state (NOT display/visibility), then reveals them
//     one-by-one on a timer, with an optional blinking caret tracking the typing front.
//
// The FULL slogan text stays in the DOM and in the accessibility tree at all times — characters are
// hidden with OPACITY, so screen readers and search engines see the complete slogan throughout — and
// the accent reserves its final width from frame one (all char spans are present, merely transparent;
// the accent is also absolutely positioned in CSS), so there is NO layout shift. Under
// prefers-reduced-motion: reduce the animation is SKIPPED entirely (the plain full slogan shows
// immediately). If this script never runs, the accent shows its normal static text — the effect is
// purely ADDITIVE and the slanted Caveat styling is unchanged.
//
// Dependency-free; bundled into assets/global.autojs.js via entry/global.js → objects/global/index.js.
// Idempotent (per-element bound flag); re-runs on shopify:section:load (the theme editor re-renders
// the section wrapper, recreating the accent element).
// =============================================================================

const ACCENT_SELECTOR = '.section-header__accent';
const CHAR_CLASS = 'section-header__accent-char';
const CHAR_PENDING = 'section-header__accent-char--pending';
const CHAR_TYPED = 'section-header__accent-char--typed';
const CARET_CLASS = 'section-header__accent-caret';
const BOUND_FLAG = 'shAccentTypingBound'; // dataset key → data-sh-accent-typing-bound

const STEP_MS = 60; // delay between characters
const START_DELAY_MS = 140; // small pause before the first character (caret blinks during it)
const CARET_LINGER_MS = 700; // caret keeps blinking briefly after the last char, then is removed
const VISIBLE_THRESHOLD = 0.5; // fraction of the accent in view before typing starts

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Recursively wrap every character of every descendant TEXT node in a `--pending` (hidden) span,
// preserving element wrappers so inline_richtext markup survives. Returns the char spans in document
// order so the caller can reveal them sequentially. Array.from() splits by code point (surrogate-safe).
function wrapChars(node, collected) {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const chars = Array.from(child.textContent);
      if (chars.length === 0) return;
      const fragment = document.createDocumentFragment();
      chars.forEach((char) => {
        const span = document.createElement('span');
        span.className = `${CHAR_CLASS} ${CHAR_PENDING}`;
        span.textContent = char;
        collected.push(span);
        fragment.appendChild(span);
      });
      node.replaceChild(fragment, child);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      if (child.classList && child.classList.contains(CARET_CLASS)) return; // defensive: never re-split a caret
      wrapChars(child, collected);
    }
  });
}

function typeAccent(accent) {
  const chars = [];
  wrapChars(accent, chars);
  if (chars.length === 0) return; // whitespace-only accent — nothing to reveal, leave as rendered

  // Optional blinking caret — an ABSOLUTELY POSITIONED overlay (see _section-header.scss) that sits at
  // the typing front, blinks via CSS, then is removed when done. Being out of flow it contributes zero
  // to the accent's width / line-box, so moving or removing it never shifts the slogan (no end-of-anim
  // jump). aria-hidden so it never leaks into the (already-complete) accessible slogan text.
  const caret = document.createElement('span');
  caret.className = CARET_CLASS;
  caret.setAttribute('aria-hidden', 'true');
  accent.appendChild(caret); // out of flow → DOM position is irrelevant; JS drives its coords

  // Position the caret at a glyph edge using LAYOUT offsets. offsetLeft/offsetTop are in the accent's own
  // pre-transform coordinate space (they ignore the CSS rotate), and each char's offsetParent is the
  // absolutely positioned accent — so this lands the caret correctly under the slant. `trailing` puts it
  // just after the char (the typed front); otherwise at the char's leading edge (the pre-typing start).
  const caretAt = (span, trailing) => {
    caret.style.left = `${span.offsetLeft + (trailing ? span.offsetWidth : 0)}px`;
    caret.style.top = `${span.offsetTop}px`;
  };

  caretAt(chars[0], false); // start: caret at the leading edge of the first (still-hidden) char

  let index = 0;
  const step = () => {
    const span = chars[index];
    span.classList.remove(CHAR_PENDING);
    span.classList.add(CHAR_TYPED);
    caretAt(span, true); // move caret to just after the char just typed
    index += 1;

    if (index < chars.length) {
      window.setTimeout(step, STEP_MS);
    } else {
      window.setTimeout(() => {
        if (caret.parentNode) caret.parentNode.removeChild(caret);
      }, CARET_LINGER_MS);
    }
  };

  window.setTimeout(step, START_DELAY_MS);
}

function bindAccent(accent) {
  if (accent.dataset[BOUND_FLAG] === 'true') return; // idempotent across re-inits

  // Opt-in gate: the block setting (animate_accent, default on → data-animate-accent='true') AND
  // the accent actually has text. Anything else shows the plain static slogan, untouched.
  if (accent.getAttribute('data-animate-accent') !== 'true') return;
  if (accent.textContent.trim().length === 0) return;
  accent.dataset[BOUND_FLAG] = 'true';

  // Reduced motion (or no IntersectionObserver) → show the full slogan immediately, no animation.
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target); // once per element
        obs.disconnect();
        typeAccent(entry.target);
      });
    },
    { threshold: VISIBLE_THRESHOLD }
  );
  observer.observe(accent);
}

function initSectionHeaderTyping() {
  document.querySelectorAll(ACCENT_SELECTOR).forEach(bindAccent);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSectionHeaderTyping);
} else {
  initSectionHeaderTyping();
}
document.addEventListener('shopify:section:load', initSectionHeaderTyping);

export { bindAccent };
export default initSectionHeaderTyping;
